import { DEVICE } from '@/shared/config';
import { DEVICE_STATUS } from '@/entities/device/@x/study';
import { zeroPad } from '@/shared/lib';
import dayjs from 'dayjs';
import _ from 'lodash';
import { t } from 'ttag';
import { STUDY_TYPES, STUDY_STATUS } from './studyConstants';
import type { Device, Study } from './studyRecordTypes';

/**
 * Reports whether a study is an in-flight Biotres study, the precondition for the Biotres warnings. Excludes the DRAFT, STARTING, DONE and ABORTED statuses and requires the assigned device model to be BIOTRES.
 *
 * @param study - Study whose status and device model are inspected.
 * @returns True for an active study running on a Biotres device.
 */
const checkIsActiveStudyBiotresWarning = (study: Study) => {
  const notActiveStatuses = [
    STUDY_STATUS.DRAFT,
    STUDY_STATUS.STARTING,
    STUDY_STATUS.DONE,
    STUDY_STATUS.ABORTED,
  ];
  return !notActiveStatuses.includes(study.status) && study.device?.model === DEVICE.BIOTRES;
};

/**
 * Reports whether the study's type makes it eligible for the Biotres warnings. Treats linked studies, an info.followOnStudy or a followOnStudy field as a follow-on pair, and accepts either that case or any type other than extended Holter and plain single-leg Holter.
 *
 * @param study - Study whose type and follow-on links are inspected.
 * @returns True when the study type qualifies for the warning.
 */
const checkStudyTypeBiotresWarning = (study: Study) => {
  const isFollowOnStudies =
    study.linkedStudies?.length > 0 ||
    !_.isEmpty(study.info?.followOnStudy) ||
    !_.isEmpty(study.followOnStudy);
  const isSingleHolterStudy = study.studyType === STUDY_TYPES.HOLTER && !isFollowOnStudies;
  return (
    isFollowOnStudies || (!isSingleHolterStudy && study.studyType !== STUDY_TYPES.EXTENDED_HOLTER)
  );
};

/**
 * Reports whether the patient has already entered the reference code and paired the device for this study. For the second leg of a follow-on pair (the study whose id matches linkedStudies[1]) it accepts a first-sync timestamp on either leg, otherwise it only looks at the study's own patientFirstSyncDeviceAt.
 *
 * @param study - Study whose pairing timestamps are inspected.
 * @returns True when a first device sync has been recorded.
 */
const checkHasInputRefCode = (study: Study) => {
  const isFollowOnStudy = study.linkedStudies?.length > 0 && study.id === study.linkedStudies[1].id;
  return isFollowOnStudy
    ? !!study.patientFirstSyncDeviceAt || !!study.linkedStudies[0].patientFirstSyncDeviceAt
    : !!study.patientFirstSyncDeviceAt;
};

/**
 * Decides whether to warn that the patient has not yet entered the reference code on a Biotres study. Excludes test and Bioflux-direct studies, requires the active-Biotres and study-type checks to pass, and requires no first device sync to have been recorded.
 *
 * @param study - Study whose device, type and pairing state are evaluated.
 * @returns True when the reference-code warning applies.
 */
const checkBiotresRefCodeWarning = (study: Study) =>
  !study.isTest &&
  !study.isBiofluxDirect &&
  checkIsActiveStudyBiotresWarning(study) &&
  checkStudyTypeBiotresWarning(study) &&
  !checkHasInputRefCode(study);

/**
 * Produces the success toast text after a study reference code goes out to the patient. Says resent rather than sent when the code had already been delivered once.
 *
 * @param hasSentRefCode - Whether the code had already been sent before.
 * @returns Translated success message.
 */
const getSendRefCodeSuccessfullyMessage = (hasSentRefCode: boolean) =>
  hasSentRefCode ? t`Reference code resent successfully!` : t`Reference code sent successfully!`;

/**
 * Produces the failure toast text when sending a study reference code does not go through. Says failed to resend rather than failed to send when the code had already been delivered once.
 *
 * @param hasSentRefCode - Whether the code had already been sent before.
 * @returns Translated failure message.
 */
const getSendRefCodeUnsuccessfullyMessage = (hasSentRefCode: boolean) =>
  hasSentRefCode ? t`Failed to resend the reference code!` : t`Failed to send the reference code!`;

/**
 * Decides whether to warn that a Biotres device has dropped offline mid-study. Requires the device status to be OFFLINE with its last sync more than two hours ago, the study not to be a test, and the active-Biotres, study-type and reference-code-entered checks all to pass.
 *
 * @param study - Study whose device and type are evaluated.
 * @returns True when the offline warning applies.
 */
const checkBiotresOfflineWarning = (study: Study) =>
  study.device?.status === DEVICE_STATUS.OFFLINE &&
  dayjs().diff(study.device?.lastSync?.time, 'hours', true) > 2 &&
  !study.isTest &&
  checkIsActiveStudyBiotresWarning(study) &&
  checkStudyTypeBiotresWarning(study) &&
  checkHasInputRefCode(study);

/**
 * Reports whether a device's last sync happened over a mobile network rather than Bluetooth or USB. Matches the lastSync networkMode against the cellular generations 2G, 3G and 4G.
 *
 * @param device - Device whose lastSync networkMode is inspected.
 * @returns True when the last sync used a cellular network.
 */
const checkDeviceUsingCellular = (device: Device | undefined) => {
  // * For testing (CP-2.25.0)
  // if (device?.model === DEVICE.BIOTRES_PRO) {
  //   if (device?.lastSync?.operator === 'USB') {
  //     return true;
  //   }
  // }

  const cellularNetworks = ['2G', '3G', '4G'];
  const isDeviceUsingCellular = cellularNetworks.includes(device?.lastSync?.networkMode ?? '');
  return isDeviceUsingCellular;
};

/**
 * Decides whether a BioCore Pro device's firmware schema is recent enough to stream live ECG over cellular. Zero-pads the device's last-sync schema to eight digits and treats the trailing four digits as a build number, requiring it to be at least 8814.
 *
 * @param device - Device whose lastSync schema is checked.
 * @returns True when the schema build is 8814 or newer.
 */
const checkBiocoreProDeviceCellularStreamingSchema = (device: Device | undefined) => {
  // * For testing (CP-2.25.0)
  // if (device?.model === DEVICE.BIOTRES_PRO) {
  //   if (device?.lastSync?.operator === 'USB') {
  //     return true;
  //   }
  // }

  const formatSchema = zeroPad(device?.lastSync?.schema, 8);
  const isValidSchema = Number(formatSchema.slice(-4)) >= 8814;
  return isValidSchema;
};

/**
 * Decides whether the View live ECG action is shown at all for a study. Requires the device model to be Biotres or BioCore Pro and the study status not to be COMPLETED, DONE or ABORTED.
 *
 * @param study - Study whose device model and status are inspected.
 * @returns True when the button should be rendered.
 */
const shouldDisplayViewLiveEcgButton = (study: Study) => {
  const notDisplayedViewLiveEcgStudyStatus = [
    STUDY_STATUS.COMPLETED,
    STUDY_STATUS.DONE,
    STUDY_STATUS.ABORTED,
  ];
  const isAllowedDevices = _.includes([DEVICE.BIOTRES, DEVICE.BIOTRES_PRO], study.device?.model);
  return isAllowedDevices && !notDisplayedViewLiveEcgStudyStatus.includes(study.status);
};

/**
 * Decides whether the View live ECG action is disabled for a study and what tooltip explains it. Rejects studies still in DRAFT or STARTING, offline devices, USB-connected devices and Bioflux models outright, and for BioCore Pro rejects a cellular connection whose firmware schema is outdated or whose facility has not enabled enableLiveECGStream, otherwise reports enabled with an empty reason.
 *
 * @param study - Study whose status, device and facility are evaluated.
 * @returns Disabled flag paired with the explanatory text.
 */
const isDisabledViewLiveEcgButton = (study: Study) => {
  const notAllowedViewLiveEcgStudyStatuses = [STUDY_STATUS.DRAFT, STUDY_STATUS.STARTING];
  const isNotOngoingStudy =
    !study.status || notAllowedViewLiveEcgStudyStatuses.includes(study.status);
  if (isNotOngoingStudy) {
    return {
      disabled: true,
      disabledText: t`View live ECG is only available for on-going studies`,
    };
  }

  const isDeviceOffline = study.deviceStatus
    ? study.deviceStatus === DEVICE_STATUS.OFFLINE
    : study.device?.status === DEVICE_STATUS.OFFLINE;
  if (isDeviceOffline) {
    return {
      disabled: true,
      disabledText: t`Live ECG not available for offline devices.`,
    };
  }

  // * For testing (CP-2.25.0)
  // if (study.device?.model === DEVICE.BIOTRES_PRO) {
  //   return {
  //     disabled: false,
  //     disabledText: '',
  //   };
  // }

  const isUsbDeviceConnection = study.device?.lastSync?.operator === 'USB';
  if (isUsbDeviceConnection) {
    return {
      disabled: true,
      disabledText: t`Live ECG not available when the device is connected via USB.`,
    };
  }

  if (study.device?.model === DEVICE.BIOFLUX) {
    return {
      disabled: true,
      disabledText: t`Live ECG not available for Bioflux devices.`,
    };
  }

  if (study.device?.model === DEVICE.BIOTRES_PRO) {
    const isDeviceUsingCellular = checkDeviceUsingCellular(study.device);
    const isValidSchema = checkBiocoreProDeviceCellularStreamingSchema(study.device);
    if (isDeviceUsingCellular) {
      if (isValidSchema) {
        const { enableLiveECGStream } = study.facility ?? {};
        if (!enableLiveECGStream) {
          return {
            disabled: true,
            disabledText: t`Live ECG available only when the device is connected via Bluetooth.`,
          };
        }
      } else {
        return {
          disabled: true,
          disabledText: t`Live ECG not available for devices with outdated firmware.`,
        };
      }
    }
  }

  return {
    disabled: false,
    disabledText: '',
  };
};

/**
 * Reports whether the reference code has already gone out to the patient for this study. Returns false for an empty study, otherwise true for any status past DRAFT and STARTING (which send the code automatically) or when sentReferenceCodeAt is set.
 *
 * @param study - Study whose status and send timestamp are inspected.
 * @returns True when the reference code has been sent.
 */
const checkHasSentRefCode = (study: Study) => {
  if (_.isEmpty(study)) {
    return false;
  }

  // *: Started study has sent reference code automatically
  return (
    ![STUDY_STATUS.DRAFT, STUDY_STATUS.STARTING].includes(study.status) ||
    !!study.sentReferenceCodeAt
  );
};

/**
 * Builds the confirmation copy warning that edits propagate to the End of Use, Notification and Event reports of the study. Pairs that main message with a note about signing physician amendment notes when a signature is required, otherwise with the plain are-you-sure prompt.
 *
 * @param withSignature - Whether the signature note replaces the confirm prompt.
 * @returns Paragraph fragment with the confirmation text.
 */
const getContent = (withSignature = false) => {
  const mainMessage = t`These changes will be reflected in all existing and future reports for this study, including the End of Use Report, Notification Report, and Event Report.`;

  const signMessage = t`To apply changes, you must provide your signature when editing Physician amendment notes.`;

  const confirmMessage = t`Are you sure you want to apply the changes?`;

  if (withSignature) {
    return (
      <>
        <p>{mainMessage}</p>
        <p>{signMessage}</p>
      </>
    );
  }

  return (
    <>
      <p>{mainMessage}</p>
      <p>{confirmMessage}</p>
    </>
  );
};

export {
  checkBiocoreProDeviceCellularStreamingSchema,
  checkBiotresOfflineWarning,
  checkBiotresRefCodeWarning,
  checkDeviceUsingCellular,
  checkHasSentRefCode,
  getContent,
  getSendRefCodeSuccessfullyMessage,
  getSendRefCodeUnsuccessfullyMessage,
  isDisabledViewLiveEcgButton,
  shouldDisplayViewLiveEcgButton,
};
